// src/padplanner/planVocabulary.js
//
// Labels and formatting for plans. The values are the server's (147's CHECK
// constraints, routes/padplanner.mjs PLAN_TYPES / PLAN_STATUSES); only the
// words shown for them live here.

// With its extension: admin-api imports this module under plain Node (provenance.test, submitImagery.test).
import { copernicusAttribution } from "../lib/map/attribution.js";

export const PLAN_TYPE_LABELS = Object.freeze({
  drilling: "Drilling pad",
  completions: "Completions",
  flowback: "Flowback",
  facility: "Facility",
  other: "Other",
});
export const PLAN_TYPES = Object.freeze(Object.keys(PLAN_TYPE_LABELS));

// All five exist in the schema from S2 on (decision 3); only "draft" can be
// reached until the review slice. The list tabs show every one, with its count.
export const STATUS_LABELS = Object.freeze({
  draft: "Draft",
  in_review: "In review",
  changes_requested: "Changes requested",
  approved: "Approved",
  issued: "Issued",
});
export const PLAN_STATUSES = Object.freeze(Object.keys(STATUS_LABELS));

const M2_PER_ACRE = 4046.8564224;

/** "12.4 acres" -- the unit a pad is talked about in. */
export function formatAcres(m2) {
  if (m2 === null || m2 === undefined || !Number.isFinite(Number(m2))) return null;
  const acres = Number(m2) / M2_PER_ACRE;
  return `${acres < 10 ? acres.toFixed(2) : acres.toFixed(1)} acres`;
}

/** 4238312345 -> 42-383-12345 */
export function formatApi10(api10) {
  return typeof api10 === "string" && /^\d{10}$/.test(api10)
    ? `${api10.slice(0, 2)}-${api10.slice(2, 5)}-${api10.slice(5)}`
    : api10 ?? "";
}

/** A date-only value (YYYY-MM-DD) as written, never through a time zone. */
export function formatDate(d) {
  if (!d) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d));
  if (!m) return String(d);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
}

/** A timestamp, in the viewer's local time. */
export function formatUpdated(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** "Saved 5 min ago": the plan page's header, from updated_at. A day or more is a date. */
export function formatSaved(ts, now = Date.now()) {
  const t = ts ? new Date(ts).getTime() : NaN;
  if (!Number.isFinite(t)) return null;
  const sec = Math.max(0, Math.round((now - t) / 1000));
  if (sec < 60) return "Saved just now";
  if (sec < 3600) return `Saved ${Math.floor(sec / 60)} min ago`;
  if (sec < 86400) { const h = Math.floor(sec / 3600); return `Saved ${h} hour${h === 1 ? "" : "s"} ago`; }
  return `Saved ${formatUpdated(ts)}`;
}

/** "Saved 3:41 PM": the editor's header, from the save response's plan.updated_at. A day or more is a date. */
export function formatSavedAt(ts, now = Date.now()) {
  const d = ts ? new Date(ts) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  if (now - d.getTime() >= 86400000) return `Saved ${formatUpdated(ts)}`;
  return `Saved ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
}

// ── A CAPTURE IS NAMED, NEVER KEYED ────────────────────────────────────────
//
// A person reads "Basemap 10 cm · 7 Jun 2026", never a dataset key such as
// imagery.permian.midland.2026_06_07. The product word comes from the SKU's
// family, the resolution from the SKU (imagery_skus.default_resolution_cm,
// which the server sends), and the date from acquisition_date. A SKU of no
// known family reads "Imagery": a new product shows a plain word, not its code.

const SKU_FAMILIES = [["BASEMAP", "Basemap"], ["HIRES", "Hi-res"], ["S2", "Sentinel-2"], ["DRONE", "Drone"]];
const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** 10 -> "10 cm", 1000 -> "10 m"; null for none. */
export function formatResolution(cm) {
  const n = Number(cm);
  if (cm === null || cm === undefined || !Number.isFinite(n) || n <= 0) return null;
  return n < 100 ? `${n} cm` : `${Number((n / 100).toFixed(1))} m`;
}

// ── ONE PARSER FOR A CAPTURE'S DATE ────────────────────────────────────────
//
// It comes in two spellings. The epochs answer passes aiprime.datasets'
// acquisition_date through as stored, which is text: "2026_06_07". A pinned
// capture's date is pad_plan_revisions.imagery_acquisition_date, a date the
// server casts to text: "2026-06-07". Anything else (a month, a timestamp,
// mixed separators, an impossible day) is not parsed: the caller shows
// UNDATED in its place, and the capture is shown regardless of its date.
export const UNDATED = "Undated";

/** "2026_06_07" or "2026-06-07" -> {year, month, day}; null for anything else. */
export function parseAcquisitionDate(d) {
  const m = typeof d === "string" ? /^(\d{4})([-_])(\d{2})\2(\d{2})$/.exec(d) : null;
  if (!m) return null;
  const [year, month, day] = [Number(m[1]), Number(m[3]), Number(m[4])];
  // Date.UTC rolls 31 Feb over into March; a date that rolls over is not a date.
  const t = new Date(Date.UTC(year, month - 1, day));
  if (t.getUTCFullYear() !== year || t.getUTCMonth() !== month - 1 || t.getUTCDate() !== day) return null;
  return { year, month, day };
}

/** "2026_06_07" or "2026-06-07" -> "7 Jun 2026"; null when it does not parse. */
export function formatImageryDate(d) {
  const p = parseAcquisitionDate(d);
  return p ? `${p.day} ${SHORT_MONTHS[p.month - 1]} ${p.year}` : null;
}

// ── SYNTHETIC TEST IMAGERY (DI4, migration 168) ────────────────────────────
//
// datasets.provenance "synthetic_test" is a raster made to exercise the
// pipeline: it shows some other ground. It is never a default (openingCapture
// skips it), it is drawn only when pinned or picked in the strip, and wherever
// it is drawn it says so: the strip's SYNTHETIC chip and the label below. No
// banner on the map (S-e, DB 2026-11-02, amending DI4); the issued PDF's
// sentence is admin-api/lib/imagery/provenance.js's alone.
export const SYNTHETIC_TEST = "synthetic_test";
/** A capture, lane entry or pin whose dataset is synthetic_test. */
export const isSyntheticCapture = (c) => c?.provenance === SYNTHETIC_TEST;
/**
 * The entitled mask's include (footprint restyle S-b): the dataset_id of the
 * capture shown, when it is synthetic; else null. GET /imagery/entitled leaves
 * a synthetic dataset out unless it is included, so without this the mask
 * would darken the capture the page has drawn.
 *
 * The pin answers for itself (revision.imagery.dataset_id, from the plan
 * answer): the imagery list names only captures whose footprint contains the
 * plan's point, and a pin need not be one of them. A capture picked in the
 * strip is in the list by definition, so it answers from there.
 * @param {{pin?: object|null, lanes?: object|null, key?: string|null}} shown
 */
export function includeForCapture({ pin = null, lanes = null, key = null } = {}) {
  if (!key) return null;
  const c = pin?.dataset_key === key ? pin : lanes?.collects?.find((e) => e.dataset_key === key);
  return isSyntheticCapture(c) ? c.dataset_id ?? null : null;
}

/**
 * The entitled mask's with_reference (footprint restyle S-c4): the dataset_id
 * of the capture shown, when it is a Sentinel-2 composite (product_class
 * "reference", the strip's "context only" lane); else null. Without it the
 * mask would dim the composite's entitled area under the page's own capture.
 * Answered like creditForCapture: a picked capture from the lanes, the pin
 * for itself (revision.imagery carries product_class and dataset_id).
 * @param {{pin?: object|null, lanes?: object|null, key?: string|null}} shown
 */
export function referenceForCapture({ pin = null, lanes = null, key = null } = {}) {
  if (!key) return null;
  const fromLanes = lanes?.reference?.find((e) => e.dataset_key === key);
  const c = fromLanes ?? (pin?.dataset_key === key ? pin : lanes?.collects?.find((e) => e.dataset_key === key));
  const reference = Boolean(fromLanes) || c?.product_class === "reference";
  return c && reference ? c.dataset_id ?? null : null;
}

/**
 * The credit the capture shown owes on the map (footprint restyle S-e slice
 * 2): Copernicus's line, for its year, when it is a reference composite
 * (Sentinel-2, product_class "reference", the lane the strip marks "context
 * only"; Explore credits the same class the same way); else null. The map
 * puts it on the capture's tile source, so the credits footer shows it
 * exactly while the capture is drawn. Copernicus attribution is a license
 * condition of the Sentinel data. Answered like includeForCapture: the pin
 * for itself, a picked capture from the lanes.
 * @param {{pin?: object|null, lanes?: object|null, key?: string|null}} shown
 */
export function creditForCapture({ pin = null, lanes = null, key = null } = {}) {
  if (!key) return null;
  const fromLanes = lanes?.reference?.find((e) => e.dataset_key === key);
  const c = fromLanes ?? (pin?.dataset_key === key ? pin : lanes?.collects?.find((e) => e.dataset_key === key));
  const reference = Boolean(fromLanes) || c?.product_class === "reference";
  return c && reference ? copernicusAttribution(c.acquisition_date) : null;
}

/** A capture's product, without its date: "Basemap 10 cm", "Sentinel-2 10 m", "Imagery". */
export function captureProduct(capture) {
  const sku = String(capture?.sku_code ?? "").toUpperCase();
  const family = SKU_FAMILIES.find(([prefix]) => sku.startsWith(prefix))?.[1] ?? "Imagery";
  return [family, formatResolution(capture?.resolution_cm)].filter(Boolean).join(" ");
}

/**
 * "Basemap 10 cm · 7 Jun 2026", or "Basemap 10 cm · Undated"; a synthetic
 * capture is "SYNTHETIC TEST · Drone 1 cm · 31 Oct 2026".
 * @param {{sku_code?: string|null, resolution_cm?: number|null, acquisition_date?: string|null, provenance?: string|null}|null} capture
 * @returns {string|null} null for no capture
 */
export function imageryLabel(capture) {
  if (!capture) return null;
  const text = `${captureProduct(capture)} · ${formatImageryDate(capture.acquisition_date) ?? UNDATED}`;
  return isSyntheticCapture(capture) ? `SYNTHETIC TEST · ${text}` : text;
}

// An empty imagery strip says WHY (fidelity item 6). The epochs answer's
// imagery_entitled tells the two apart: an org with no active imagery
// entitlement sees nothing anywhere, which is not the same as nothing here.
export const IMAGERY_NOT_ENTITLED_TEXT = "Your organization has no imagery entitlement.";
export const IMAGERY_NOT_COVERED_TEXT = "No imagery you can view covers this plan.";
export function imageryEmptyText(imageryEntitled) {
  return imageryEntitled === false ? IMAGERY_NOT_ENTITLED_TEXT : IMAGERY_NOT_COVERED_TEXT;
}

/** A time-strip chip's text: the capture date, "7 Jun 2026", or UNDATED. */
export function chipDate(epoch) {
  return formatImageryDate(epoch?.acquisition_date) ?? UNDATED;
}

/**
 * The capture a map opens on: the pin, or with none the newest collect (the
 * server lists collects oldest first). Showing one is not pinning it. Never a
 * reference epoch: Sentinel-2 is context only. Never a synthetic_test one
 * either (DI4): with no pin, the newest collect that is not synthetic, so a
 * synthetic capture is shown only when it is pinned or picked. The submit's
 * pin follows the same rule (admin-api submitImagery.js openingCaptureKey,
 * held to this function by its test).
 */
export function openingCapture(pinnedKey, lanes) {
  if (pinnedKey) return pinnedKey;
  const collects = (Array.isArray(lanes?.collects) ? lanes.collects : []).filter((c) => !isSyntheticCapture(c));
  return collects.length ? collects[collects.length - 1].dataset_key : null;
}
